import { useEffect, useRef, useState } from "react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { getApiUrl } from "@/lib/config";
import { crmFetch, type CrmRecord } from "@/lib/crm/api";
import { useCrmText } from "@/lib/crm/i18n";
import { cn } from "@/lib/utils";
import { Initials } from "./crm-ui";
import { Camera, ImagePlus, Loader2, Trash2 } from "lucide-react";

// Object URLs of photos already fetched in this session: `${contactId}:${photoUpdatedAt}`.
const photoCache = new Map<string, string>();

/** Photo of a contact (fetched with the session token), or null while loading / when it has none. */
export function useContactPhoto(contactId: string | null | undefined, version: string | null | undefined): string | null {
  const key = contactId && version ? `${contactId}:${version}` : null;
  const [url, setUrl] = useState<string | null>(() => (key ? photoCache.get(key) ?? null : null));
  useEffect(() => {
    if (!key) { setUrl(null); return; }
    const hit = photoCache.get(key);
    if (hit) { setUrl(hit); return; }
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(getApiUrl(`/api/crm/contacts/${contactId}/photo?v=${encodeURIComponent(version!)}`), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const objectUrl = URL.createObjectURL(await res.blob());
        photoCache.set(key, objectUrl);
        if (alive) setUrl(objectUrl);
      } catch { /* offline: initials stay */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return url;
}

/**
 * Square JPEG of at most `size` px cut from the middle of the picked image. Drawing on a canvas also
 * drops the file's metadata (camera, GPS) before it leaves the browser.
 */
export async function squarePhoto(file: File, size = 320): Promise<Blob> {
  const src = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = src;
    await img.decode();
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    if (!side) throw new Error("empty image");
    const out = Math.min(size, side);
    const canvas = document.createElement("canvas");
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";   // transparent PNGs become white, not black, as JPEG
    ctx.fillRect(0, 0, out, out);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, out, out);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), "image/jpeg", 0.86));
  } finally {
    URL.revokeObjectURL(src);
  }
}

/** Round photo (or initials) of a contact. */
export function ContactPhoto({ contactId, version, name, className }: { contactId?: string | null; version?: string | null; name: string; className?: string }) {
  const url = useContactPhoto(contactId, version);
  // A stored file the browser cannot draw (sent straight to the API) falls back to the initials.
  const [broken, setBroken] = useState<string | null>(null);
  if (url && broken !== url) {
    return <img src={url} alt={name} onError={() => setBroken(url)} className={cn("h-12 w-12 shrink-0 rounded-full object-cover", className)} data-testid="contact-photo" />;
  }
  return <Initials name={name} className={className} />;
}

/** Record header avatar of a contact: photo with a camera button to add, change or remove it. */
export function ContactAvatar({ record, name, editable, onChanged }: {
  record: CrmRecord | undefined;
  name: string;
  editable: boolean;
  onChanged: (r: CrmRecord) => void;
}) {
  const { tx } = useCrmText();
  const { toast } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const id = record?.id;
  const version = (record?.values.photoUpdatedAt as string | null) || null;

  const upload = async (file: File) => {
    if (!id) return;
    if (!/^image\/(jpeg|png|webp|gif|bmp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(file.name)) {
      toast({ title: tx("photoInvalid"), variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const blob = await squarePhoto(file);
      const form = new FormData();
      form.append("file", blob, "photo.jpg");
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl(`/api/crm/contacts/${id}/photo`), {
        method: "PUT",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || res.statusText);
      onChanged(json as CrmRecord);
      toast({ title: tx("photoSaved") });
    } catch (e) {
      toast({ title: tx("photoInvalid"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    setBusy(true);
    try {
      onChanged(await crmFetch<CrmRecord>("DELETE", `/api/crm/contacts/${id}/photo`));
      toast({ title: tx("photoRemoved") });
    } catch (e) {
      toast({ title: tx("error"), description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative shrink-0" data-testid="contact-avatar">
      <ContactPhoto contactId={id} version={version} name={name} className="h-14 w-14" />
      {busy && (
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-background/60">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
      {editable && id && (
        <>
          <input ref={input} type="file" accept="image/*" className="hidden" data-testid="contact-photo-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
          {version ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" title={tx("changePhoto")} disabled={busy}
                  className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border bg-card shadow-sm hover:bg-muted"
                  data-testid="contact-photo-menu">
                  <Camera className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => input.current?.click()} data-testid="contact-photo-change">
                  <ImagePlus className="mr-2 h-4 w-4" /> {tx("changePhoto")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={remove} data-testid="contact-photo-remove">
                  <Trash2 className="mr-2 h-4 w-4 text-destructive" /> {tx("removePhoto")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button type="button" title={tx("addPhoto")} disabled={busy} onClick={() => input.current?.click()}
              className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border bg-card shadow-sm hover:bg-muted"
              data-testid="contact-photo-add">
              <Camera className="h-3.5 w-3.5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}
